import React, {
    useContext, useCallback, useEffect, useState
} from 'react';
import _ from 'lodash';
import stereotype from 'stereotype';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { DriverLicenseService } from 'gw-capability-driverlicense';
import { MockUpUtil, EntityUtil } from 'gw-portals-util-js';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import styles from 'gw-capability-policyjob-react/components/DriverComponent/DriverComponent.module.scss';

// eslint-disable-next-line import/no-unresolved
import config from 'app-config';

import metadata from './DriversPage.metadata.json5';
import messages from './DriversPage.messages';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';

function DriversPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const translator = useContext(TranslatorContext);
    const [isPageInitialized, setPageInitialized] = useState(false);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const [showAccountHolderToggle, updateShowAccountHolderToggle] = useState(false);
    const [existingDrivers, setExistingDrivers] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState({});    
    const [driverPath, setDriverPath] = useState('');

    const { authHeader } = useAuthentication();
    const {
        initialValidation,
        onValidate,
        isComponentValid,
        disregardFieldValidation
    } = useValidation('DriverPage');

    const writeValue = useCallback(
        (value, path) => {
            const vmPath = path.replace(/\.value$/, '');
            const inputCtrlType = _.get(submissionVM, `${vmPath}.aspects.inputCtrlType`);
            let effectiveValue;
            switch (inputCtrlType) {
                case 'boolean':
                case 'number':
                    effectiveValue = stereotype(value);
                    break;
                default:
                    effectiveValue = value;
                    break;
            }
            _.set(submissionVM, path, effectiveValue);
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const createDriverVM = useCallback( async () => {
        const {quoteID} = submissionVM.value;
        if (_.isEmpty(submissionVM.lobData.businessAuto.coverables.drivers.value)) {
            const accountHolder = submissionVM.baseData.accountHolder.value;
            let driverObj = {isValidationRequired: true};
            if (submissionVM.baseData.accountHolder.value.subtype === "Person") {
                let licenseClassificationData = await LoadSaveService.updateLicenseClassDetails(
                    quoteID,
                    accountHolder.publicID,
                    authHeader
                );
                driverObj = {
                    accidents: '0',
                    violations: '0',
                    lastName: accountHolder.firstName,
                    firstName: accountHolder.lastName,
                    dateOfBirth: accountHolder.dateOfBirth,
                    isPolicyHolder: true,
                    person: accountHolder,
                    isValidationRequired: true,
                    licenseState: 'ON',
                    //licenseClassificationRecords: [{isValidationRequired: true}],
                    sameAsAccountHolder: true,
                    licenseNumber: accountHolder.licenseNumber || '',
                    gender: accountHolder.gender || '',
                    maritalStatus: accountHolder.maritalStatus || '',
                    licenseStatus: accountHolder.licenseStatus,
                    firstLicenseDate: accountHolder.firstLicenseDate,
                    occupation: accountHolder.occupation,
                    typeOfEmployment_EY: accountHolder.typeOfEmployment_EY,
                    licenseClassificationRecords: licenseClassificationData || [{isValidationRequired: true}]
                };
            } else {
                driverObj = {
                    accidents: '0',
                    violations: '0',
                    isPolicyHolder: false,
                    person: {},
                    isValidationRequired: true,
                    licenseState: 'ON',
                    licenseClassificationRecords: [{isValidationRequired: true}]
                };
            }
            const { _dtoName, _xCenter } = submissionVM.lobData.businessAuto.coverables.drivers;
            const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);
            driverVM.priorCancelledRecords.value = submissionVM.value.baseData.priorCancelledRecords;
            submissionVM.lobData.businessAuto.coverables.drivers.pushElement(driverVM);
            updateWizardData(submissionVM);
        }
    }, [submissionVM, updateWizardData, viewModelService]);

    useEffect(() => {
        const driversPath = 'lobData.businessAuto.coverables.drivers';

        if (
            MockUpUtil.isValueMocked(
                submissionVM.value,
                'quote.ba',
                `${driversPath}.0.licenseNumber`
            )
        ) {
            const driverPath = `${driversPath}.value[0]`;
            const driver = _.get(submissionVM, driverPath);

            const newDriver = {
                ...driver,
                licenseNumber: undefined,
                yearLicensed: undefined,
                gender: undefined,
                licenseState: 'ON'
            };
            _.set(submissionVM, driverPath, newDriver);
            updateWizardData(submissionVM);
        }
       

        let drivers = _.get(submissionVM, `${driversPath}.value`);
        drivers = _.orderBy(drivers, ['isPolicyHolder'], ['desc']);
        _.each(drivers, (driver) => {
            driver.isValidationRequired = true;
        });
        if (_.isEmpty(drivers)) {
            createDriverVM();
            updateShowAccountHolderToggle(true);
        }

        const _existingDrivers = submissionVM.baseData.account.accountContacts.value.map((c) => {
            c['id'] = c.publicID;
            return c;
        });
        const availableDrivers = [{ id: "placeholder", displayName: "Create New Driver" }].concat(_existingDrivers);
        setExistingDrivers(availableDrivers);
        setPageInitialized(true);
        
        // No array dependencies needed in this hook.
        // The logic of initializing drivers data needs to be executed only once
        // when landing into drivers page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionVM , setExistingDrivers]);

    const onNext = useCallback(async () => {
        _.unset(submissionVM.value, 'bindData');
        const accountHolderPath = 'baseData.accountHolder.value';
        const driversPath = 'lobData.businessAuto.coverables.drivers.value';
        const accountHolder = _.get(submissionVM, accountHolderPath);
        const driversList = _.get(submissionVM, driversPath);
        driversList.forEach((driver, index) => {
            if (
                _.isEmpty(driver.person.displayName)
                || !driver.person.displayName.includes(
                    `${driver.person.firstName} ${driver.person.lastName}`
                )
            ) {
                const displayname = `${driver.person.firstName} ${driver.person.lastName}`;
                _.set(submissionVM, `${driversPath}[${index}].person.displayName`, displayname);
            }
        });
        // const accountHolderDriverIndex = _.findIndex(driversList, { isPolicyHolder: true });
        // const accountHolderDriverPath = `${driversPath}[${accountHolderDriverIndex}]`;
        // const accountHolderDriver = _.get(submissionVM, accountHolderDriverPath);

        // const commonObjects = _.pick(accountHolderDriver, [
        //     'gender',
        //     'licenseNumber',
        //     'licenseState'
        // ]);
        // const commonObjectsForDriver = _.pick(accountHolder, ['firstName', 'lastName']);
        // const newDriver = { ...accountHolderDriver, ...commonObjects, ...commonObjectsForDriver };

        // _.set(submissionVM, accountHolderDriverPath, newDriver);
        // _.set(submissionVM, accountHolderPath, { ...accountHolder, ...commonObjects });
        // _.set(
        //     submissionVM,
        //     `${accountHolderDriverPath}.person`,
        //     _.get(submissionVM, accountHolderPath)
        // );
        submissionVM.value = await LoadSaveService.updateDraftSubmission(
            submissionVM.value,
            authHeader
        );
        return submissionVM;
    }, [submissionVM]);

    const addDriver = useCallback(() => {
        const driverObj = {
            accidents: '0',
            violations: '0',
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true,
            licenseState: 'ON',
            licenseClassificationRecords: [{isValidationRequired: true}]
        };
        const { _xCenter, _dtoName } = submissionVM.lobData.businessAuto.coverables.drivers;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);
        driverVM.person = {};
        driverVM.driverConvictionDetails = [];
        submissionVM.lobData.businessAuto.coverables.drivers.pushElement(driverVM);
        updateWizardData(submissionVM);
    }, [submissionVM, updateWizardData, viewModelService]);

    const removeDriver = useCallback(
        (evt) => {
            const driverListPath = 'lobData.businessAuto.coverables.drivers.value';
            const driverPath = evt.path;
            const driver = _.get(submissionVM, driverPath).value;
            const driverList = _.get(submissionVM, driverListPath);
            const driverIndex = driverList.findIndex(
                (driverFromList) => _.isEqual(driverFromList, driver)
            );

            ModalNextProvider.showConfirm({
                title: messages.removeDriverTitle,
                message: messages.removeDriverDescription,
                status: 'warning',
                icon: 'mi-error-outline'
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }
                    const newDriverList = _.remove(driverList,
                        (driverFromList) => _.isEqual(driverFromList, driver));

                    _.set(submissionVM, newDriverList, driverListPath);
                    disregardFieldValidation(`driver${driverIndex}`);
                    updateWizardData(submissionVM);
                    return true;
                }, _.noop
            );
        },
        [disregardFieldValidation, submissionVM, updateWizardData]
    );

    const onFileUpload = useCallback(
        async (file, index) => {
            const driverPath = `lobData.businessAuto.coverables.drivers.children[${index}]`;
            const driverLicenseData = await DriverLicenseService.uploadBarcode(file);
            const driverVM = _.get(submissionVM, driverPath);

            Object.entries(driverLicenseData).forEach(([key, value]) => {
                if (_.has(driverVM, key)) {
                    _.set(driverVM, `${key}.value`, value);
                }
                if (_.has(driverVM.person, key)) {
                    _.set(driverVM.person, `${key}.value`, value);
                }
            });
            writeValue(driverVM.value, `${driverPath}.value`);
        },
        [submissionVM, writeValue]
    );

    const generateOverrides = useCallback(() => {
        const drivers = _.get(submissionVM, 'lobData.businessAuto.coverables.drivers.value', []);
        const overrideProps = {};
        let readOnlyFields = ['driverFirstName', 'driverMiddleName', 'driverLastName', 'driverDateOfBirth'];

        if (config.persona === 'producer' || config.persona === 'csr') {
            readOnlyFields = ['driverFirstName', 'driverMiddleName', 'driverLastName'];
        }

        drivers.forEach((driver, index) => {
            const isAccountHolder = driver.isPolicyHolder && driver.sameAsAccountHolder;

            let driverTitle = translator(messages.baDriverTitle);

            if (drivers.length > 1 && index !== 0) {
                driverTitle = `${translator(messages.baDriverTitle)} ${index + 1}`;
            }

            overrideProps[`driver${index}`] = {
                readOnlyFields: isAccountHolder ? readOnlyFields : null,
                submissionVM: submissionVM,
                updateWizardData: updateWizardData,
                isFromNewSubmission: true,
                showAccountHolderToggle
            };
            overrideProps[`scanDriverLicenseBarcodeContainer${index}`] = {
                visible: !isAccountHolder && (breakpoint === 'phone' || breakpoint === 'tablet')
            };
            overrideProps[`baDriverTitle${index}`] = {
                value: driverTitle
            };
            overrideProps[`scanDriverLicenseBarcode${index}`] = {
                onValueChange: undefined,
                onUpload: (file) => onFileUpload(file, index),
                maxFileSize: config.maxFileUploadSize
            };
            overrideProps[`baDeleteDriver${index}`] = {
                visible: drivers.length > 1
            };
        });

        return overrideProps;
    }, [breakpoint, onFileUpload, submissionVM, translator]);

    
    const setDefaultNewEntry = useCallback((driver) => {
        let driverObj = {
            accidents: '0',
            violations: '0',
            isPolicyHolder: false,
            tempID: EntityUtil.nextId(),
            isValidationRequired: true,
            sameAsAccountHolder: false,
            licenseState: 'ON',
            licenseClassificationRecords: [{isValidationRequired: true}]
        };

        if (driver) {
            driverObj = Object.assign(driverObj, driver)
        }

        const { _xCenter, _dtoName } = submissionVM.lobData.businessAuto.coverables.drivers;
        const driverVM = viewModelService.create(driverObj, _xCenter, _dtoName);
        driverVM.person = driver ? driverVM.person : {};
        driverVM.driverConvictionDetails = [];
        submissionVM.lobData.businessAuto.coverables.drivers.pushElement(driverVM);
        setSelectedDriver(driverVM);
    }, [submissionVM.value.lobData.businessAuto.coverables, setSelectedDriver]);


    const onAddNew = useCallback((driver) => {
        setDefaultNewEntry(driver);
        const newVehicleIndex = submissionVM.lobData.businessAuto.coverables.drivers.length - 1;
        setDriverPath(`lobData.businessAuto.coverables.drivers.children.${newVehicleIndex}`);
        _.set(
            submissionVM.value,
            `lobData.businessAuto.coverables.drivers[${newVehicleIndex}].accidents`,
            '0'
        );
        _.set(
            submissionVM.value,
            `lobData.businessAuto.coverables.drivers[${newVehicleIndex}].violations`,
            '0'
        );
        updateWizardData(submissionVM);
    }, [setDefaultNewEntry, submissionVM, updateWizardData]);

    const addExistingDriver = async(value) => {   
        const {quoteID} = submissionVM.value;             
        if (value == 'placeholder') {
            onAddNew();
            setIsEdit(true);
            return
        }
        const driver = submissionVM.baseData.account.accountContacts.value.filter((c) => {
            return c.publicID == value
        })[0];

        if(driver){
            let licenseClassificationData = await LoadSaveService.updateLicenseClassDetails(
                    quoteID,
                    driver.publicID,
                    authHeader
                );
            const _driver = {
                sameAsAccountHolder: false, //driver.accountHolder,
                dateOfBirth: driver.dateOfBirth,
                gender: driver.gender,
                licenseNumber: driver.licenseNumber,
                licenseState: driver.licenseState,
                maritalStatus: driver.maritalStatus,
                licenseStatus: driver.licenseStatus,
                firstLicenseDate: driver.firstLicenseDate,
                occupation: driver.occupation,
                typeOfEmployment_EY: driver.typeOfEmployment_EY,
                licenseClassificationRecords: licenseClassificationData,
                person: {
                    displayName: driver.displayName,
                    firstName: driver.firstName,
                    lastName: driver.lastName,
                    primaryPhoneType: driver.primaryPhoneType || undefined,
                    workNumber: driver.workNumber || undefined,
                    homeNumber: driver.homeNumber || undefined,
                    publicID: driver.publicID,
                }
            }
    
            onAddNew(_driver);
            setIsEdit(true);
        }
        
    }

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: false,
            showRequired: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        addDriver: {
            disabled: !isComponentValid
        },
        driverContacts: {             
            availableValues: existingDrivers,
            onValueChange: addExistingDriver,
            searchable: true
        },
        ...generateOverrides()
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [overrideProps, submissionVM]
    );

    const resolvers = {
        resolveCallbackMap: {
            onAddDriverClick: addDriver,
            onRemoveDriver: removeDriver,
            onValidate: onValidate
        }
    };

    if (!isPageInitialized) {
        return null;
    }

    return (
        <WizardPage onNext={onNext} skipWhen={initialValidation} disableNext={!submissionVM.aspects.subtreeValid}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

DriversPage.propTypes = wizardProps;
export default DriversPage;
